import { useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { adminApi } from "./api";
import { hasPermission, useAuth } from "./auth";

/**
 * The console shell.
 *
 * Menu items are filtered by permission so an editor does not see an ERP link
 * that would 403 — a menu that offers what the API refuses reads as a bug, not
 * as a boundary. The server still checks every request; this is only about not
 * lying to the user.
 */

interface NavEntry {
  to: string;
  label: string;
  index: string;
  permission: string;
}

const NAV: NavEntry[] = [
  { to: "/admin", label: "Overview", index: "00", permission: "dashboard.read" },
  { to: "/admin/content/post", label: "Journal", index: "01", permission: "cms.read" },
  { to: "/admin/content/project", label: "Projects", index: "02", permission: "cms.read" },
  { to: "/admin/content/turnkey", label: "Turn-Key", index: "03", permission: "cms.read" },
  { to: "/admin/content/plan", label: "Floor Plans", index: "04", permission: "cms.read" },
  { to: "/admin/content/service", label: "Services", index: "05", permission: "cms.read" },
  { to: "/admin/content/job", label: "Careers", index: "06", permission: "cms.read" },
  { to: "/admin/content/page", label: "Pages", index: "07", permission: "cms.read" },
  { to: "/admin/content/team", label: "Team", index: "08", permission: "cms.read" },
  { to: "/admin/media", label: "Media", index: "09", permission: "cms.read" },
  { to: "/admin/settings", label: "Settings", index: "10", permission: "cms.read" },
];

export function AdminLayout() {
  const navigate = useNavigate();
  const { user, permissions, setIdentity, signOut } = useAuth();

  // Identity is re-fetched on mount rather than trusted from storage: roles
  // change, accounts get deactivated, and the cached copy would still draw a
  // menu the server no longer honours.
  const me = useQuery({
    queryKey: ["admin", "me"],
    queryFn: adminApi.auth.me,
    staleTime: 60_000,
    retry: false,
  });

  useEffect(() => {
    if (me.data) setIdentity(me.data.user, me.data.permissions);
  }, [me.data, setIdentity]);

  const visible = NAV.filter((item) => hasPermission(permissions, item.permission));

  async function handleSignOut() {
    try {
      await adminApi.auth.logout();
    } finally {
      // Sign out locally even if the request failed — the user asked to leave.
      signOut();
      navigate("/admin/login", { replace: true });
    }
  }

  return (
    <div className="adm">
      <aside className="adm__side">
        <div className="adm__brand">
          <span className="adm__brandname">Indraprastha</span>
          <span className="adm__brandsub">Content Console</span>
        </div>

        <nav className="adm__nav" aria-label="Console">
          <ul>
            {visible.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === "/admin"}
                  className={({ isActive }) => `adm__link${isActive ? " is-active" : ""}`}
                >
                  <span className="adm__linknum">{item.index}</span>
                  <span>{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="adm__user">
          <span className="adm__username">{user?.full_name ?? me.data?.user.full_name ?? "—"}</span>
          <span className="adm__useremail">{user?.email ?? ""}</span>
          <button type="button" className="adm__signout" onClick={handleSignOut}>
            Sign out
          </button>
        </div>
      </aside>

      <main className="adm__main">
        <Outlet />
      </main>
    </div>
  );
}
